import { c as _c } from "react/compiler-runtime";
import { attributeLabel, attributeValueLabel, formatProductAttributes, specCategoryAndType } from "@osmix/catalog-core/attributes";
import { useQuery } from "@tanstack/react-query";
import { Layers, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type Product, type ProductCollectionParticipation } from "@/api";
import { ErrorState } from "@/components/common/states";
import { ColorSwatches } from "@/components/product/color-swatches";
import { OutfitCard } from "@/components/product/outfit-card";
import { Price } from "@/components/product/price";
import { ProductPromoPrice, useProductPromo } from "@/components/promo/product-promo";
import { categoryPath } from "@/lib/category-tree";
import { trackProductView } from "@/lib/discovery";
import { looksLikeUgcArticle, type UgcNavState } from "@/lib/ugc-search";
import { ecDetail } from "@/lib/metrika-ecommerce";
import { useCategoryTree } from "@/lib/use-category-tree";
import { DiscoverySourceProvider } from "@/lib/discovery-source";
import { useGoBack } from "@/lib/history-back";
import { IDEA_PARAM, rememberIdeaOrigin } from "@/lib/idea-origin";
import { productHref, VARIANT_PARAM } from "@/lib/product-link";
import { ProductCard } from "@/components/product/product-card";
import { ProductGrid } from "@/components/product/product-grid";
import { ProductDelivery } from "@/components/product/product-delivery";
import { CustomizationSheet, CustomizeInChatDialog } from "@/components/product/customization-sheet";
import { ProductShopTab } from "@/components/product/product-shop-tab";
import { RatePurchasedBar } from "@/components/product/rate-purchased-bar";
import { ProductReviewsPanel, type ProductSocialTab } from "@/components/product/product-social";
import { useProductQuestions } from "@/components/product/product-questions";
import { PhotoZoom } from "@/components/product/photo-zoom";
import { GALLERY_FRAME_RATIO, ProductGallery } from "@/components/product/product-gallery";
import { type AdItem, mixAds, useAdPlacements, withoutAdProducts } from "@/lib/ad-tracking";
import { resolveSizePick, sizeAvailability, sizeChipLabel, sizeTableRows } from "@/lib/product-size";
import { SizeTableDialog } from "@/components/product/size-table-dialog";
import { SizeChip } from "@/components/product/size-chip";
import { PhotoHotspots } from "@/components/product/photo-hotspots";
import { visibleBadges } from "@/lib/product-badges";
import { type AxisPicks, axisValueAvailable, axisValues, initialAxisPicks, matchesPicks, missingAxis, resolveAxisPick } from "@/lib/product-axes";
import { Skeleton } from "@osmix/ui/skeleton";
import { BagIcon, ChevronDown16Icon, ChevronLeftIcon, FavoriteHeartIcon, HeartLine16Icon, Minus20Icon, Plus20Icon, Share20Icon, StarIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCart, usePreferences, useProfile, useWishlist } from "@/store";
import { AddToLookDialog } from "@/components/looks/look-dialogs";
import { ideaLikeId } from "@/store";
import { useIdeaHidden, useVisibleIdeas } from "@/store/hidden-ideas";
import { IdeaWall, SaveHeartButton } from "./feed";
import { notifyAddedToCart } from "@/lib/cart-toast";
import { productIdFromParam } from "@osmix/lib/seo";
import { usePageMeta } from "@/lib/use-page-meta";
import { orderQty } from "@/lib/min-order";

// Миниатюра варианта 55×73 r12 с обводкой (цвет обводки — у вызова).
const VARIANT_TILE_CLASS = "block h-[73px] w-[55px] shrink-0 overflow-hidden rounded-[12px] border bg-white outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ds-focus";
export function ProductPage() {
  const $ = _c(9);
  useTranslation();
  const t0 = useParams().id ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = productIdFromParam(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const id = t1;
  usePageMeta();
  let t2;
  if ($[2] !== id) {
    t2 = {
      queryKey: ["products", "get", id],
      queryFn: () => api.products.get(id)
    };
    $[2] = id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const product = useQuery(t2);
  if (product.isPending) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ProductSkeleton />;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (product.error || !product.data) {
    let t3;
    if ($[5] !== product) {
      t3 = <ErrorState onRetry={() => product.refetch()} />;
      $[5] = product;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t3;
  if ($[7] !== product.data) {
    t3 = <ProductView key={product.data.id} product={product.data} />;
    $[7] = product.data;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// Split so the inner view can hold per-product local state (selected size,
// quantity) and reset cleanly when the route id changes (via `key`).
function ProductView({
  product
}: {
  product: Product;
}) {
  const {
    t,
    i18n
  } = useTranslation();
  const navigate = useNavigate();
  // Карточку открыли по прямой ссылке - «Назад» ведёт в каталог, а не с сайта.
  const goBack = useGoBack("/catalog");
  const shops = useQuery({
    queryKey: ["shops", "list"],
    queryFn: () => api.shops.list()
  });
  // Действующая акция магазина (П3): цена со скидкой и таймер до конца.
  const activePromo = useProductPromo(product);
  // Путь категории товара (Женщинам / Обувь / Кроссовки и кеды) — из дерева API.
  const {
    categories
  } = useCategoryTree();
  const categoryTrail = product.categoryId ? categoryPath(categories, product.categoryId) : [];
  const shop = shops.data?.find(s => s.id === product.shopId);
  const seller = shop ?? (product.sellerInfo ? {
    id: product.sellerInfo.shopId,
    slug: product.sellerInfo.slug,
    name: product.sellerInfo.name,
    rating: product.sellerInfo.rating
  } : undefined);

  // pageSize disambiguates this key from the shop page's 60-item full listing
  // for the same shop — they must not share a cache entry.
  const related = useQuery({
    queryKey: ["products", "list", product.shopId, 8],
    queryFn: () => api.products.list({
      shopId: product.shopId,
      pageSize: 8
    })
  });
  const productCollections = useQuery({
    queryKey: ["feed", "productCollections", product.id],
    queryFn: () => api.feed.productCollections(product.id)
  });
  // «Похожие товары» — одна категория (семантически близкие), не только этот
  // магазин. «Подборка от OSMIX» — общая свежая выдача каталога.
  const similar = useQuery({
    queryKey: ["products", "list", "similar", product.categoryId],
    queryFn: () => api.products.list({
      categoryId: product.categoryId,
      pageSize: 8
    })
  });
  // Реклама в рекомендациях (fd-promo-26): место «recommendations» отдают
  // пакеты «Расширенный» и «Максимальный охват» (и CPM) — шлюз сам решает,
  // какие кампании сюда попадают. Открытый товар рекламой не показываем.
  const city = usePreferences(s_0 => s_0.city);
  const recommendationAds = useAdPlacements({
    place: "recommendations",
    categoryId: product.categoryId,
    city,
    limit: 2,
    exclude: [product.id]
  });
  const osmixPick = useQuery({
    // Сетка 4 × 3 (кадр); 13 — с запасом на сам товар, если он в выдаче.
    queryKey: ["products", "list", "osmix-pick", 13],
    queryFn: () => api.products.list({
      sort: "newest",
      pageSize: 13
    })
  });
  // «Похожие идеи с этим товаром» — мозаика идей из ленты вдохновения.
  const feedCollections = useQuery({
    queryKey: ["feed", "collections"],
    queryFn: () => api.feed.collections()
  });
  // «Собери образ» — остальные товары той подборки/идеи, где встречается этот
  // товар (complete-the-look). Берём первую подборку с составом.
  const outfitSource = (productCollections.data ?? []).find(c => c.productIds.length > 1);
  const outfitIds = (outfitSource?.productIds ?? []).filter(id => id !== product.id).slice(0, 5);
  const outfit = useQuery({
    queryKey: ["products", "byIds", "outfit", outfitSource?.id ?? "none"],
    queryFn: () => api.products.byIds(outfitIds),
    enabled: outfitIds.length > 0
  });
  // Сводка отзывов для рейтинг-строки вверху инфо-панели (★ 4.5 · N оценок).
  const reviewSummary = useQuery({
    queryKey: ["products", "reviews", product.id],
    queryFn: () => api.products.reviews(product.id)
  });
  const ratingAvg = reviewSummary.data?.summary.average ?? 0;
  const ratingCount = reviewSummary.data?.summary.count ?? 0;
  const addToCart = useCart(s_1 => s_1.add);
  const cartItems = useCart(s_2 => s_2.cart?.items);
  const setCartQty = useCart(s_3 => s_3.setQty);
  const removeCartItem = useCart(s_4 => s_4.remove);
  const saved = useWishlist(s_5 => s_5.ids.includes(product.id));
  const toggleProduct = useWishlist(s_6 => s_6.toggleProduct);
  const setProductLikedOnServer = useWishlist(s_7 => s_7.setProductLikedOnServer);
  const buyerToken = useProfile(s_8 => s_8.token);

  /*
   * Выбранный вариант живёт в адресе: `?variant=<id>`.
   *
   * Каждая карточка каталога — один вариант товара, и её ссылка ведёт сюда с
   * `?variant=`: страница открывается ровно на нём (фото, цена, размеры этого
   * цвета). Плитки вариантов — тоже ссылки, меняющие `?variant=` (Айталы 02.10:
   * «выбор варианта внутри карточки — это ссылки на те товары»), поэтому
   * «Поделиться» и обновление страницы сохраняют выбор. Адрес меняем через
   * replace: «Назад» уводит со страницы, а не перебирает варианты.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const variants = product.variants ?? [];
  const defaultVariant = variants.find(variant => variant.stock > 0) ?? variants[0];
  const urlVariantId = searchParams.get(VARIANT_PARAM);
  const selectedVariant = variants.find(variant_0 => variant_0.id === urlVariantId) ?? defaultVariant;
  const variantId = selectedVariant?.id ?? "";
  const variantSearch = (id_0: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(VARIANT_PARAM, id_0);
    return `?${next.toString()}`;
  };
  const setVariantId = (id_1: string) => {
    if (id_1 === variantId) return;
    setSearchParams(prev => {
      const next_0 = new URLSearchParams(prev);
      next_0.set(VARIANT_PARAM, id_1);
      return next_0;
    }, {
      replace: true,
      preventScrollReset: true
    });
  };
  // Пустой размер (незаполненная строка «Цена и склад» в кабинете) — не
  // плашка: шлюз их уже не отдаёт, это защита для старых ответов (Айталы
  // 13.10, photo_69).
  const sizes = product.sizes.filter(s_9 => s_9.trim() !== "");
  /*
   * Плитки «Вариант товара» — по ЦВЕТУ, а не по SKU.
   *
   * Вариант на шлюзе — это цвет × размер, и плитка на каждый SKU повторяла одно
   * фото столько раз, сколько у цвета размеров, а клик по размеру переключал
   * подсвеченную плитку (Айталы 01.10: «при нажатии на размер постоянно
   * меняется вариант»). В макете вариант — это цвет, размер выбирается внутри
   * него. Варианты без цвета остаются плиткой каждый, как раньше.
   */
  const tileKey = (v: {
    id: string;
    color?: string | null;
  }) => v.color?.trim().toLowerCase() || v.id;
  const selectedKey = selectedVariant ? tileKey(selectedVariant) : "";
  const colorTiles = variants.filter((v_0, i) => variants.findIndex(x => tileKey(x) === tileKey(v_0)) === i);
  const sameTile = variants.filter(v_1 => tileKey(v_1) === selectedKey);
  /*
   * Товары-варианты группы (Айталы 16.10: «каждый вариант товара — отдельный
   * товар со своим артикулом»). Плитка соседнего товара — ссылка на ЕГО
   * страницу; на месте этого товара — его собственные цвета (у старой
   * многоцветной карточки их несколько, у новой — один).
   */
  type GroupEntry = {
    current: true;
  } | (NonNullable<Product["groupVariants"]>[number] & {
    current: false;
  });
  const groupEntries: GroupEntry[] = (product.groupVariants ?? []).some(item_0 => item_0.current) ? (product.groupVariants ?? []).map(item => item.current ? {
    current: true
  } as const : {
    ...item,
    current: false as const
  }) : [{
    current: true
  }];
  const siblingTile = (item_1: Extract<GroupEntry, {
    current: false;
  }>) => {
    const label = item_1.color || item_1.name;
    return <Link key={`group-${item_1.id}`} to={productHref(item_1.id)} replace data-testid="group-variant-tile" aria-label={label || undefined} title={label} className={cn(VARIANT_TILE_CLASS, "border-osmix-gray-300 hover:border-[#BBBBBB]",
    // Закончился — плитка бледная, но ведёт на страницу: там «Нет в
    // наличии» и подписка на поступление.
    !item_1.available && "opacity-40")}>
				{item_1.photo ? <img src={item_1.photo} alt="" className="size-full object-cover" /> : <span className="flex size-full items-center justify-center bg-osmix-gray-100 type-small text-osmix-icon">
						{item_1.color || item_1.id}
					</span>}
			</Link>;
  };
  // Размер из `?variant=` сам не выбирается: вариант из каталога задаёт цвет,
  // а размер покупатель выбирает явно (Айталы 02.10: «размеры не выбраны, а
  // товары уже в корзине»). Исключение — размер у товара или у цвета один.
  const [size, setSize] = useState(() => {
    if (sizes.length === 1) return sizes[0] ?? "";
    const only = sameTile.length === 1 ? sameTile[0]?.size : undefined;
    return only && sizes.includes(only) ? only : "";
  });
  // «В корзину» без размера: красная надпись «Выберите размер» под кнопкой
  // (Айталы 02.10, A-15), без обводки блока размеров (A-16).
  const [sizeHint, setSizeHint] = useState(false);
  /*
   * Оси вариантов сверх цвета и размера (fd-catalog-07): материал и свой
   * параметр продавца. Выбор - чипами под размерами, внутри выбранного
   * цвета; размер × значения осей указывают на одну строку (SKU). Ось с
   * одним значением выбрана сразу, по ссылке `?variant=` - значения строки.
   */
  const axes = product.variantAxes ?? [];
  const axisScope = colorTiles.length > 1 ? sameTile : variants;
  const [axisPicks, setAxisPicks] = useState<AxisPicks>(() => initialAxisPicks(axes, axisScope, urlVariantId ? selectedVariant : undefined));
  const axisMissing = missingAxis(axes, axisScope, axisPicks);
  const pickAxis = (key: string, value: string) => {
    const next_1 = {
      ...axisPicks,
      [key]: value
    };
    setAxisPicks(next_1);
    setSizeHint(false);
    const match = resolveAxisPick(axisScope, {
      size: size || undefined,
      picks: next_1
    });
    if (match && match.id !== selectedVariant?.id) setVariantId(match.id);
  };
  // Доступность размеров — внутри выбранного цвета: размер, которого нет у
  // этого цвета в наличии, не выбрать.
  const sizeStock = sizeAvailability(axisScope.filter(v_2 => matchesPicks(v_2, axisPicks)));
  // «Таблица размеров» (как в приложении): мерки размеров выбранного цвета,
  // которые шлюз считает по сетке категории. Нет мерок — нет и ссылки.
  const sizeRows = sizeTableRows(colorTiles.length > 1 ? sameTile : variants, product.sizeMeasures).filter(row => sizes.includes(row.size));
  const [sizeTableOpen, setSizeTableOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  // Вкладка панели «Отзывы и вопросы» (fd-review-02/03).
  const [socialTab, setSocialTab] = useState<ProductSocialTab>("reviews");
  const openSocial = (tab: ProductSocialTab) => {
    setSocialTab(tab);
    setReviewsOpen(true);
  };
  const questionCount = useProductQuestions(product.id).data?.items.length ?? 0;
  // Чат с продавцом — общий раздел «Чаты»: тред по товару или черновик нового.
  const openSellerChat = () => navigate(`/chats?product=${encodeURIComponent(product.id)}`);
  // Количество на карточке товара фиксировано 1 (регулируется в корзине — как в макете).
  // Минимальный заказ (миграция 422) - сразу N: меньше сервер не примет.
  const minQty = orderQty(product);
  const qty = minQty;
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  // Другой вариант (плитка, размер, «Назад» браузера по адресу) — галерея с
  // первого фото.
  useEffect(() => {
    if (variantId) setActive(0);
  }, [variantId]);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [lookOpen, setLookOpen] = useState(false);
  // Таб-панель под инфо-блоком (кадры 3561:144759, 4052:41290, 4052:40245):
  // Характеристики / Описание / Доставка / О магазине.
  const [infoTab, setInfoTab] = useState<"specs" | "description" | "delivery" | "shop">("specs");
  const [showAllAttrs, setShowAllAttrs] = useState(false);
  const displayPhotos = selectedVariant?.photos.length ? selectedVariant.photos : product.photos;
  const displayMedia: ProductMediaItem[] = [...displayPhotos.map(src => ({
    type: "photo" as const,
    src
  })), ...(product.videoUrl ? [{
    type: "video" as const,
    src: product.videoUrl
  }] : [])];
  const displayPrice = selectedVariant?.price ?? product.price;
  const displayOldPrice = selectedVariant?.oldPrice ?? product.oldPrice;
  // Пока размер не выбран, «N шт. в наличии» — по цвету целиком, а не по
  // SKU, подставленному ради фото и цены.
  const displayStock = selectedVariant && (sizes.length > 1 && !size || axisMissing) ? axisScope.filter(v_3 => (!size || v_3.size === size) && matchesPicks(v_3, axisPicks)).reduce((sum, v_4) => sum + Math.max(0, v_4.stock), 0) : selectedVariant?.stock ?? product.stock;
  const soldOut = displayStock < minQty;
  // Размер обязателен, когда их больше одного — даже если варианты есть:
  // первый вариант подставлен только ради фото и цены, а не как выбор покупателя.
  const needsSize = variants.length > 0 ? !selectedVariant || sizes.length > 1 && !size : sizes.length > 1 && !size;
  const pickSize = (next_2: string) => {
    setSize(next_2);
    setSizeHint(false);
    // Варианты, разложенные по размерам: чип размера выбирает SKU того же
    // цвета (иначе в корзину уезжал первый вариант, а не выбранный размер).
    // Другой цвет берётся, только если у текущего такого размера нет вовсе.
    const bySize = resolveSizePick(variants, next_2, selectedVariant?.color);
    // С осями (материал) - строка этого размера с выбранными значениями.
    const match_0 = axes.length ? resolveAxisPick(axisScope, {
      size: next_2,
      picks: axisPicks
    }) ?? bySize : bySize;
    if (match_0 && match_0.id !== selectedVariant?.id) setVariantId(match_0.id);
  };

  /*
   * Кастомизация ПЕРЕВЕШИВАЕТ «под заказ».
   *
   * Гейтвей выводит `madeToOrder` как «есть исключение по возврату ИЛИ включена
   * кастомизация», поэтому товар, который просто можно изменить, надевал чужое
   * оформление: пилюлю «Под заказ» и кнопку «Заказать изготовление» вместо
   * корзины. Борд разводит эти случаи (§2.2 против §2.3): у кастомизируемого
   * товара пилюли нет, главная кнопка — «В корзину», а рядом строка
   * «Доступна кастомизация». Мобильный клиент уже приведён к этому правилу;
   * витрина отставала, и купить такой товар в вебе было нельзя вовсе.
   */
  const customizable = product.customization?.enabled === true;
  const customizationOptions = customizable ? product.customization?.options ?? [] : [];
  // Таблица изменений есть — после «В корзину» она выплывает слева (кадр
  // 3870:26009); нет — окно «напишите продавцу в чате» (кадр 3870:25479).
  const hasCustomization = customizationOptions.length > 0;
  const isMadeToOrder = product.madeToOrder === true && !customizable;
  // Выбор изменений: название → количество. Ноль означает «не выбрано».
  const [customQty, setCustomQty] = useState<Record<string, number>>({});
  const [customOpen, setCustomOpen] = useState(false);
  const [chatHintOpen, setChatHintOpen] = useState(false);
  const customizationList = customizationOptions.map(option => ({
    name: option.name,
    qty: customQty[option.name] ?? 0
  })).filter(row_0 => row_0.qty > 0);

  /*
   * Позиция корзины с этим вариантом — кнопка «В корзину» становится
   * счётчиком «− N +» (Айталы 02.10, A-15). Пока размер не выбран, счётчика
   * нет: вариант подставлен только ради фото и цены. Позиции «под заказ» по
   * предложению продавца (customOfferId) — отдельная история, их не считаем.
   */
  const cartLine = needsSize || axisMissing ? undefined : cartItems?.find(item_2 => item_2.productId === product.id && !item_2.customOfferId && (selectedVariant ? item_2.variantId === selectedVariant.id : !size || item_2.size === size));
  const [qtyBusy, setQtyBusy] = useState(false);
  const changeQty = async (delta: 1 | -1) => {
    if (!cartLine || qtyBusy) return;
    setQtyBusy(true);
    try {
      const next_3 = cartLine.qty + delta;
      // Минус на единице (или на минимальном заказе) убирает позицию — кнопка снова «В корзину».
      if (next_3 < minQty) await removeCartItem(cartLine.id);else await setCartQty(cartLine.id, next_3);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setQtyBusy(false);
    }
  };
  const cartRequest = (lineQty: number, list: {
    name: string;
    qty: number;
  }[]) => ({
    productId: product.id,
    variantId: selectedVariant?.id,
    size: selectedVariant?.size ?? (size || sizes[0] || ""),
    color: selectedVariant?.color ?? product.color,
    qty: lineQty,
    // Шлём только название и количество: цену подставит сервер из карточки
    // товара.
    customization: list.length > 0 ? list : undefined
  });

  /*
   * Выбор в таблице кастомизации сразу пишется в строку корзины (кадр
   * 3870:26442 «Изменение добавлено в корзину»).
   *
   * Отдельного «обновить изменения» у корзины нет: повторное добавление того
   * же варианта ЗАМЕНЯЕТ выбор и прибавляет штуку (services/cart.ts), поэтому
   * добавляем одну штуку с новым выбором и возвращаем прежнее количество. Снять
   * выбор целиком добавлением нельзя — тогда строка пересоздаётся без него.
   */
  const [applying, setApplying] = useState(false);
  const appliedKey = useRef("");
  // Открыть таблицу на том, что уже лежит в корзине (после перезагрузки
  // страницы локальный выбор пуст, а у строки корзины он есть).
  const openCustomization = () => {
    const fromCart = new Map((cartLine?.customization ?? []).map(c_0 => [c_0.name, c_0.qty]));
    const picked: Record<string, number> = {};
    for (const option_0 of customizationOptions) picked[option_0.name] = fromCart.get(option_0.name) ?? 0;
    setCustomQty(picked);
    appliedKey.current = JSON.stringify(customizationOptions.map(option_1 => ({
      name: option_1.name,
      qty: picked[option_1.name] ?? 0
    })).filter(row_1 => row_1.qty > 0));
    setCustomOpen(true);
  };
  const pickedKey = JSON.stringify(customizationList);
  // Запись — по смене выбора (pickedKey) и строки корзины; остальное читается
  // на момент записи (количество — из стора), лишние зависимости гоняли бы её
  // на каждый рендер.
  // biome-ignore lint/correctness/useExhaustiveDependencies: см. выше
  useEffect(() => {
    if (!customOpen || !cartLine || pickedKey === appliedKey.current) return;
    const lineId = cartLine.id;
    const list_0 = customizationList;
    const timer = setTimeout(() => {
      // Количество — на момент записи: счётчик могли нажать, пока шёл отсчёт.
      const line = useCart.getState().cart?.items.find(item_3 => item_3.id === lineId);
      if (!line) return;
      const previous = appliedKey.current;
      appliedKey.current = pickedKey;
      setApplying(true);
      void (async () => {
        try {
          if (list_0.length > 0) {
            try {
              await addToCart(cartRequest(1, list_0));
              await setCartQty(line.id, line.qty);
            } catch {
              // Последняя штука на складе: «+1» не пройдёт — пересоздаём строку.
              await removeCartItem(line.id);
              await addToCart(cartRequest(line.qty, list_0));
            }
          } else if (previous && previous !== "[]") {
            await removeCartItem(line.id);
            await addToCart(cartRequest(line.qty, []));
          }
        } catch (e_0) {
          appliedKey.current = previous;
          toast.error(e_0 instanceof Error ? e_0.message : t("common.error"));
        } finally {
          setApplying(false);
        }
      })();
    }, 450);
    return () => clearTimeout(timer);
  }, [customOpen, pickedKey, cartLine?.id]);
  const add = async () => {
    if (needsSize || axisMissing) {
      setSizeHint(true);
      return;
    }
    if (soldOut) return;
    setBusy(true);
    try {
      await addToCart(cartRequest(qty, customizationList));
      appliedKey.current = JSON.stringify(customizationList);
      if (hasCustomization) {
        // Кастомизация: таблица изменений выплывает слева, её сводка и есть
        // подтверждение «в корзине».
        openCustomization();
      } else if (customizable) {
        setChatHintOpen(true);
      } else {
        // «Добавлено в корзину» (A-17): тот же тост, что у идеи — «Перейти в
        // корзину / Продолжить покупки». Кадра Айталы пока нет.
        notifyAddedToCart(t, () => navigate("/cart"), {
          photo: displayPhotos[0] ?? product.photos[0],
          brand: product.brand,
          name: product.name
        });
      }
    } catch (e_1) {
      toast.error(e_1 instanceof Error ? e_1.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const toggleSaved = () => {
    // В избранное сохраняем с выбранным размером. Приоритет — у явно выбранного
    // чипа размера (у обуви есть и чипы 36–41, и варианты; дефолтный вариант мог
    // бы затереть выбор пользователя), иначе — размер выбранного варианта.
    // Если размеров несколько, а чип не выбран, размера нет: вариант по
    // умолчанию подставлен ради фото и цены, и его размер (XS, 28, 34) уезжал в
    // избранное как выбранный (A-28, 02.10.2026).
    const chosenSize = size || (sizes.length > 1 ? undefined : selectedVariant?.size) || undefined;
    const liked = toggleProduct(product.id, chosenSize);
    void setProductLikedOnServer(product.id, liked, chosenSize);
  };
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.name,
          text: product.description,
          url
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("product.shareCopied"));
      }
    } catch (e_2) {
      if (e_2 instanceof DOMException && e_2.name === "AbortError") return;
      toast.error(t("common.error"));
    }
  };

  // Просмотр карточки товара — вторая ступень воронки продавца и «Просмотры»
  // в аналитике товара. Считаем по открытию страницы, а не по переходу: на
  // карточку приходят и по прямой ссылке, и из поиска, и из письма.
  // Пришли из идеи (`?idea=<id>` в ссылке) — просмотр и дальнейшее «в корзину»
  // засчитываются ей; без параметра привязка берётся из хранилища сессии.
  const ideaId = searchParams.get(IDEA_PARAM);
  // Открыта сразу после резолва цифрового UGC-артикула (поиск кладёт код в
  // состояние навигации; ссылка ?ugc=<код> - то же самое). UI не меняется,
  // меняется только событие просмотра: «Просмотр карточки по UGC-артикулу».
  const navState = useLocation().state as UgcNavState | null;
  const ugcRaw = navState?.ugcCode ?? searchParams.get("ugc") ?? "";
  const ugcCode = looksLikeUgcArticle(ugcRaw) ? ugcRaw.trim() : undefined;
  // biome-ignore lint/correctness/useExhaustiveDependencies: просмотр - один раз на открытие карточки, поля товара для Метрики берутся на этот момент
  useEffect(() => {
    if (ideaId) rememberIdeaOrigin(product.id, {
      collectionId: ideaId
    });
    trackProductView(product.id, undefined, ugcCode ? {
      source: "ugc_article",
      ugcArticleCode: ugcCode
    } : undefined);
    // Метрика: просмотр товара для отчёта «Электронная коммерция».
    ecDetail({
      id: product.id,
      name: product.name,
      price: product.price,
      brand: product.brand,
      category: product.category
    });
  }, [product.id, ideaId, ugcCode]);

  // Товарные рельсы макета (в порядке сверху вниз). Каждая рендерится только при
  // наличии данных — источники разные, чтобы наборы не дублировались.
  const outfitItems = (outfit.data ?? []).filter(p => p.id !== product.id).slice(0, 5);
  const collectionItems = (related.data?.data ?? []).filter(p_0 => p_0.id !== product.id);
  const similarItems = (similar.data?.data ?? []).filter(p_1 => p_1.id !== product.id);
  const osmixItems = (osmixPick.data?.data ?? []).filter(p_2 => p_2.id !== product.id).slice(0, 12);
  // Скрытые покупателем идеи (A-26) на карточке не показываем: стена ленты
  // без скрытых до загрузки — useVisibleIdeas, скрытые за эту сессию и идеи
  // товара (другой тип данных) отсеивает обёртка UnlessHidden.
  const visibleWall = useVisibleIdeas(feedCollections.data);
  const productIdeas = visibleWall.pending ? [] : productCollections.data ?? [];
  const similarIdeas = visibleWall.pending ? [] : visibleWall.ideas;
  /*
   * Характеристики (Айталы 13.10, photo_70). Раскрыты только строки макета
   * «Карточка товара в наличии»: Категория, Тип, Цвет, Пол (у товара под
   * заказ и с кастомизацией перед ними «Тип товара» и «Срок изготовления» —
   * кадры 3870:20920, 3870:26659). Незаполненная строка не рисуется. Всё
   * остальное — сезон, назначение, состав, гарантия, артикул — только под
   * «Показать ещё».
   */
  // «Тип» (itemType, 19.10.2026) — своя строка раскрытых характеристик, в
  // «Показать ещё» не дублируется.
  const {
    gender: genderRaw,
    itemType,
    ...otherAttributes
  } = product.categoryAttributes ?? {};
  const genderValue = genderRaw != null && genderRaw !== "" ? attributeValueLabel("gender", genderRaw, i18n.language) : "";
  // Коды характеристик кабинета («season: summer») — человеческими словами.
  const attributes = formatProductAttributes(otherAttributes, i18n.language);
  const spec = specCategoryAndType(categoryTrail.length ? categoryTrail : [{
    name: product.category ?? ""
  }], itemType);
  // Бейджи - словами («Хит», «Новинка») рядом с названием, а не кодами шлюза
  // во вкладке «Описание» (fd-catalog-20); во вкладке остаются теги.
  const productBadges = visibleBadges(product.badges);
  const chips = [...(product.tags ?? [])];
  const richBlocks = product.descriptionRich?.blocks.filter(block => block.text.trim()) ?? [];
  // Подпись под миниатюрами — название цвета выбранного варианта (Айталы 02.10,
  // A-18: «вариант товара под миниатюрой — это цвет»), наличие серым.
  const variantColor = selectedVariant?.color?.trim() || product.color?.trim() || "";
  // «Тип товара» — первой строкой характеристик у кастомизируемых и
  // изготавливаемых на заказ товаров (кадры 3870:20920, 3870:26659).
  const productType = customizable ? t("product.customizationAvailable") : isMadeToOrder ? t("product.madeToOrderBadge") : "";
  const tabs = [["specs", t("product.attributes")], ["description", t("product.description")], ["delivery", t("product.deliveryTab", {
    defaultValue: "Доставка"
  })], ["shop", t("product.aboutShop")]] as const;
  return (
    // Все сетки товаров ниже — рельсы рекомендаций («Товары из этой
    // коллекции», «Похожие товары», «Подборка от OSMIX»). Покупатель, ушедший
    // с карточки по такой рельсе, нашёл товар в рекомендациях.
    <DiscoverySourceProvider value="recommendations">
		<div className="flex flex-col">
			<PhotoZoom images={displayPhotos} index={active} open={zoomOpen} onClose={() => setZoomOpen(false)} onIndex={setActive} />
			{/* «Крошки» (кадр 3561:144759): «‹ Назад» — шеврон 24 + 14/18 w600 #1F1F1F
       с зазором 6, рядом через 12 — путь категории; до карточки 20px.
       Всё от левого края страницы, как шапка и ленты ниже (A-13). */}
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1 pb-5">
				<button type="button" onClick={goBack} className="flex h-6 w-fit items-center gap-1.5 rounded-sm text-[14px] font-semibold leading-[18px] text-[#1F1F1F] outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">
					<ChevronLeftIcon size={24} />
					{t("product.back", {
              defaultValue: "Назад"
            })}
				</button>
				{categoryTrail.length > 0 && <nav aria-label={t("product.category")} data-testid="product-category-path" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] leading-[18px] text-[#1F1F1F]/48">
						{categoryTrail.map((node, index) => <span key={node.id} className="flex items-center gap-2">
								{index > 0 && <span aria-hidden="true">/</span>}
								<Link to={`/catalog?categoryId=${encodeURIComponent(node.id)}`} className={cn("outline-none transition-colors hover:text-osmix-tangelo focus-visible:text-osmix-tangelo", index === categoryTrail.length - 1 && "text-[#1F1F1F]")}>
									{node.name}
								</Link>
							</span>)}
					</nav>}
			</div>

			<div className="flex flex-col gap-10">
			{/* Карточка (кадр 3561:144759, A-13): фото 560, промежуток 20, колонка
       описания 560 — от левого края страницы, а не по центру своей сетки
       (было: центр 1200 и зазор 80, фото уезжало от шапки и лент). */}
			<div className="grid gap-5 lg:grid-cols-[minmax(0,560px)_minmax(0,560px)]">
				{/* Галерея: фото листаются свайпом/прокруткой, точки внизу, нажатие —
        на весь экран с листанием (PhotoZoom). */}
				<div className="flex min-w-0 flex-col gap-3">
					<ProductGallery items={displayMedia} active={active} onActive={setActive} onOpen={i_0 => {
                setActive(i_0);
                setZoomOpen(true);
              }} alt={product.name} renderVideo={src_0 => <VideoFrame src={src_0} title={t("product.video")} />}
              // Точки на фото со ссылками на другие товары магазина (fd-catalog-03).
              renderOverlay={item_4 => item_4.type === "photo" ? <PhotoHotspots hotspots={product.photoHotspots?.find(entry => entry.photo === item_4.src)} frameRatio={GALLERY_FRAME_RATIO} /> : null}>
						{/* Лайк — «Избранное на фото» 32 (DS FavoriteHeartIcon) в углу с
          отступом 16, как в кадре (action-button 528×32 при pad 16). */}
						<button type="button" aria-pressed={saved} aria-label={saved ? t("wishlist.remove") : t("wishlist.add")} onClick={toggleSaved} className="absolute right-4 top-4 z-10 flex size-8 items-center justify-center rounded-full outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-ds-focus">
							<FavoriteHeartIcon active={saved} />
						</button>
					</ProductGallery>
				</div>

				{/* Колонка описания (кадр): блоки через 36px. */}
				<div className="flex min-w-0 flex-col gap-9">
					{/* textContainer: рейтинг, бренд, тип, цена — через 10px. */}
					<div className="flex flex-col gap-2.5">
						<div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
						{ratingCount > 0 && <div className="flex items-center gap-2.5">
								<span className="flex items-center gap-1.5">
									<StarIcon size={18} className="text-osmix-tangelo" />
									<span className="text-[16px] font-semibold leading-5 tracking-[-0.31px] text-[#161616]">
										{ratingAvg.toFixed(1)}
									</span>
								</span>
								{/* Отзывы — левая панель (кадр «Отзывы»), отдельного блока на
            странице нет. */}
								<button type="button" onClick={() => openSocial("reviews")} aria-haspopup="dialog" className="rounded-sm text-[14px] font-medium leading-[19px] tracking-[-0.28px] text-[#1F1F1F] underline underline-offset-4 outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">
									{t("product.ratingsCount", {
                        count: ratingCount
                      })}
								</button>
							</div>}
						{/* Вопросы к товару (fd-review-03) - вторичной ссылкой в той же
          строке: открывает панель «Отзывы и вопросы» на вопросах. */}
						<button type="button" onClick={() => openSocial("questions")} aria-haspopup="dialog" data-testid="open-questions" className="rounded-sm text-[14px] font-medium leading-[19px] tracking-[-0.28px] text-[#1F1F1F]/60 underline underline-offset-4 outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">
							{questionCount > 0 ? t("product.qa.questionsLink", {
                      count: questionCount
                    }) : t("product.qa.askLink")}
						</button>
						</div>
						{/* Заголовок = бренд (Innamore) 26/30 w700, подзаголовок = тип товара
          (Платье летнее) 16/20 — как в макете и в быстром просмотре. */}
						<h1 className="text-[26px] font-bold leading-[30px] tracking-[-0.31px] text-[#161616]">{product.brand}</h1>
						{/* «Под заказ» — справа от подзаголовка через 10 (макет «на заказ»):
          pill 25px r100, фон tangelo 5 %, 14/14 w500 чёрный. */}
						<div className="flex flex-wrap items-center gap-2.5">
							<span className="text-[16px] leading-5 text-[#2E2E2E]">{product.name}</span>
							{isMadeToOrder && <span className="flex h-[25px] items-center rounded-full bg-osmix-tangelo/5 px-3 text-[14px] font-medium leading-[14px] tracking-[-0.42px] text-black">
									{t("product.madeToOrderBadge")}
								</span>}
							{productBadges.map(badge => <span key={badge} data-testid="product-badge" className={cn("flex h-[25px] items-center rounded-full px-3 text-[14px] font-medium leading-[14px] tracking-[-0.42px]", badge === "sale" ? "bg-osmix-tangelo text-white" : "bg-osmix-tangelo/5 text-black")}>
									{t(`product.badges.${badge}`)}
								</span>)}
						</div>
						{activePromo ? <ProductPromoPrice productId={product.id} promo={activePromo} basePrice={displayPrice} oldPrice={displayOldPrice} /> : <Price value={displayPrice} oldValue={displayOldPrice} size="lg" pill="dark" minQty={minQty} />}
					</div>

					{/* Размеры и варианты — через 20px. */}
					{(sizes.length > 0 || variants.length > 0 || !soldOut || axes.length > 0) && <div className="flex flex-col gap-5">
							{/* Размеры «Size» (Айталы 21.10, photo_114/117) — плитки 50 в высоту r10
           #F8F8F8, шаг 60 (gap 10): сверху российский размер, под ним
           международный («42 / XS») или диапазон («One size / 44-54»);
           выбранный — тёмная «бабочка», нет в наличии — бледный. Один размер
           выбран сразу. Обводки блока нет (A-16). */}
							{sizes.length > 0 && <div data-testid="size-picker" className="flex flex-col gap-2.5">
									<div className="flex flex-wrap gap-2.5">
										{sizes.map(s_10 => <SizeChip key={s_10} label={sizeChipLabel(s_10, variants)} selected={s_10 === size} locked={sizes.length === 1}
                    // Размер, которого нет ни в одном варианте в наличии, не выбрать.
                    disabled={sizeStock.get(s_10) === 0} onSelect={() => pickSize(s_10)} />)}
									</div>
									{sizeRows.length > 0 && <>
											<button type="button" data-testid="size-table-link" onClick={() => setSizeTableOpen(true)} className="w-fit text-left text-[14px] leading-[18px] text-[#2E2E2E] underline underline-offset-4 transition-colors hover:text-osmix-tangelo">
												{t("product.sizeGuide")}
											</button>
											<SizeTableDialog open={sizeTableOpen} onOpenChange={setSizeTableOpen} rows={sizeRows} measureColumns={product.sizeMeasures?.columns ?? []} selected={size} onSelect={next_4 => {
                      pickSize(next_4);
                      setSizeTableOpen(false);
                    }} />
										</>}
								</div>}

							{/* Оси вариантов (fd-catalog-07): материал, свой параметр - чипами
           того же вида, что размеры. Значение, которого нет в наличии при
           выбранном размере, бледное и не нажимается. */}
							{axes.map(axis => {
                  const values = axisValues(axisScope, axis.key);
                  if (values.length === 0) return null;
                  return <div key={axis.key} data-testid={`axis-picker-${axis.key}`} className="flex flex-col gap-2.5">
										<span className="text-[14px] leading-[18px] text-[#2E2E2E]">
											{axis.label}
											{axisPicks[axis.key] ? `: ${axisPicks[axis.key]}` : ""}
										</span>
										<div className="flex flex-wrap gap-2.5">
											{values.map(value_0 => <SizeChip key={value_0} label={{
                        main: value_0
                      }} selected={axisPicks[axis.key] === value_0} locked={values.length === 1} disabled={!axisValueAvailable(axisScope, axis.key, value_0, {
                        size: size || undefined,
                        picks: axisPicks
                      })} onSelect={() => pickAxis(axis.key, value_0)} testId="axis-chip" />)}
										</div>
									</div>;
                })}

							{/* Варианты — фото-миниатюры 55×73 r12, шаг 65 (gap 10). У всех тонкая
           серая обводка 1px #E2E2E2 (на белом фоне товара иначе не видно
           края плитки), у выбранной — акцентная #FA5C2E (Айталы 21.10,
           photo_114/117; «подушка» clip-path снята). Плитка — цвет и ссылка
           на него (`?variant=`): берёт SKU этого цвета в выбранном размере. */}
							{variants.length > 0 || groupEntries.length > 1 ? <div className="flex flex-col gap-2.5">
									<div className="flex flex-wrap gap-2.5">
										{groupEntries.flatMap(entry_0 => entry_0.current ? colorTiles.map(variant_1 => {
                      const key_0 = tileKey(variant_1);
                      const group = variants.filter(v_5 => tileKey(v_5) === key_0);
                      const on = key_0 === selectedKey;
                      const thumb = group.find(v_6 => v_6.photos[0])?.photos[0] ?? product.photos[0];
                      const options = Object.values(variant_1.options ?? {});
                      const label_0 = (variant_1.color ? [variant_1.color, ...options] : [variant_1.size, ...options]).filter(Boolean).join(" / ");
                      const inSize = size ? group.filter(v_7 => v_7.size === size) : [];
                      const pick = (on ? selectedVariant : undefined) ?? inSize.find(v_9 => v_9.stock > 0) ?? inSize[0] ?? group.find(v_8 => v_8.stock > 0) ?? group[0];
                      const tileClass = cn(VARIANT_TILE_CLASS, on ? "border-[1.5px] border-ds-accent" : "border-osmix-gray-300 hover:border-[#BBBBBB]");
                      const body = thumb ? <img src={thumb} alt="" className="size-full object-cover" /> : <span className="flex size-full items-center justify-center bg-osmix-gray-100 type-small text-osmix-icon">
													{variant_1.color || variant_1.id}
												</span>;
                      // Цвет целиком не в наличии — плитка не ссылка.
                      if (!pick || group.every(v_10 => v_10.stock <= 0)) {
                        return <span key={key_0} title={label_0} aria-disabled="true" className={cn(tileClass, "opacity-40")}>
														{body}
													</span>;
                      }
                      return <Link key={key_0} to={{
                        search: variantSearch(pick.id)
                      }} replace preventScrollReset data-testid="variant-tile" aria-current={on ? "true" : undefined} aria-label={label_0 || undefined} title={label_0} onClick={() => {
                        if (on) return;
                        // У нового цвета нет выбранного размера — размер выбирается заново,
                        // иначе в корзину ушёл бы чужой SKU.
                        if (size && pick.size !== size && sizes.length > 1) setSize("");
                        if (!size && sizes.length > 1 && group.length === 1 && pick.size && sizes.includes(pick.size)) {
                          setSize(pick.size);
                        }
                        setSizeHint(false);
                      }} className={tileClass}>
													{body}
												</Link>;
                    }) : [siblingTile(entry_0)])}
									</div>
									{/* «Чёрный, 2 шт. в наличии»: цвет #000, наличие #000 50 %, 14/18. */}
									<p className="flex flex-wrap gap-1 text-[14px] leading-[18px]" data-testid="variant-label">
										{variantColor && <span className="text-black">{variantColor},</span>}
										{soldOut ? <span className="text-ds-error">{t("product.outOfStock")}</span> : <span className="text-black/50">{t("product.inStockCount", {
                        count: displayStock
                      })}</span>}
									</p>
								</div> : <p className="text-[14px] leading-[18px]">
									{soldOut ? <span className="text-ds-error">{t("product.outOfStock")}</span> : <span className="text-black/50">{t("product.inStockCount", {
                      count: displayStock
                    })}</span>}
								</p>}
						</div>}

					{/* Нет в наличии: сердечко = подписка «сообщить о поступлении»
         (сервер кладёт лайк закончившегося товара в список ожидания). */}
					{soldOut && !isMadeToOrder && <p className="flex items-start gap-2 type-body2 text-osmix-text-1" data-testid="waitlist-hint">
							<HeartLine16Icon className={cn("mt-0.5 shrink-0", saved ? "text-osmix-tangelo" : "text-osmix-text-1")} />
							{saved && buyerToken ? t("product.waitlistSubscribed") : buyerToken ? t("product.waitlistHint") : <span>
									{t("product.waitlistGuestHint")}{" "}
									<Link to="/profile" className="text-osmix-tangelo underline-offset-4 hover:underline">
										{t("product.waitlistSignIn")}
									</Link>
								</span>}
						</p>}

					{/* Кнопки (макет): тёмная основная 48 r12 + «Поделиться» через 20.
         Положили в корзину — основная становится счётчиком «− N +» (A-15);
         нажали без размера — под кнопкой красная «Выберите размер». */}
					<div className="flex flex-col gap-2">
						<div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:gap-x-5">
							{isMadeToOrder ?
                  // «Под заказ» (кадр 3870:26659): главная кнопка — «В чат с продавцом»,
                  // договорённость об изготовлении идёт в чате, отдельного мастера нет.
                  <Button onClick={openSellerChat} data-testid="chat-with-seller" className="min-w-[173px]">
									{t("product.chatWithSeller", {
                      defaultValue: "В чат с продавцом"
                    })}
								</Button> : cartLine ? <div data-testid="cart-stepper" className="flex h-12 w-[173px] items-center justify-between rounded-[12px] bg-ds-primary px-3 text-white">
									<button type="button" onClick={() => void changeQty(-1)} disabled={qtyBusy} aria-label={t("product.qtyLess", {
                      defaultValue: "Убрать одну"
                    })} className="flex size-8 items-center justify-center rounded-[8px] outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-60">
										<Minus20Icon />
									</button>
									<span aria-live="polite" className="text-[16px] font-semibold leading-[21px]" data-testid="cart-stepper-qty">
										{cartLine.qty}
									</span>
									<button type="button" onClick={() => void changeQty(1)} disabled={qtyBusy || cartLine.qty >= Math.max(cartLine.stock, 1)} aria-label={t("product.qtyMore", {
                      defaultValue: "Добавить ещё"
                    })} className="flex size-8 items-center justify-center rounded-[8px] outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-60">
										<Plus20Icon />
									</button>
								</div> : <Button disabled={soldOut || busy} onClick={add} data-testid="add-to-cart" aria-describedby={sizeHint ? "product-size-hint" : undefined} className="min-w-[173px]">
									<BagIcon size={20} />
									{busy ? t("product.adding") : t("product.addToCart")}
								</Button>}
							{/* «Поделиться» — прозрачная кнопка L (кадр: button #989AA2 0 %):
           share 3 20 + 16/21 w600 #1F1F1F. */}
							<Button variant="ghost" onClick={() => void share()} className="px-3 sm:px-5">
								<Share20Icon />
								{t("product.share")}
							</Button>
							{/* «В подборку» (fd-discovery-04) — та же прозрачная кнопка, что
           «Поделиться»: положить товар в свой образ или создать новый. */}
							<Button variant="ghost" onClick={() => {
                    if (!buyerToken) {
                      toast(t("product.addToLookSignIn"));
                      navigate("/profile");
                      return;
                    }
                    setLookOpen(true);
                  }} className="px-3 sm:px-5" data-testid="add-to-look">
								<Layers className="size-5" strokeWidth={1.5} aria-hidden="true" />
								{t("product.addToLook")}
							</Button>
							{lookOpen && <AddToLookDialog open={lookOpen} onClose={() => setLookOpen(false)} productId={product.id} />}
						</div>
						{sizeHint && (needsSize || axisMissing) && <p id="product-size-hint" role="alert" className="text-[14px] leading-[18px] text-ds-error" data-testid="size-hint">
								{needsSize || !axisMissing ? t("product.selectSize") : t("product.selectAxis", {
                    axis: axisMissing.label
                  })}
							</p>}
					</div>

					{/* Таб-панель Характеристики / Описание / Доставка / О магазине (кадры
         3561:144759, 4052:41290, 4052:40245): база 496×1 #1F1F1F 8 %, вкладки
         через 24, 16/21; активная w600 #1F1F1F с линией 1px, остальные w500
         #1F1F1F 48 %. Содержимое — через 12. Доставка — вкладкой, а не
         отдельной серой плашкой (A-14). */}
					<div className="flex flex-col gap-3">
						<div className="flex max-w-[496px] gap-6 overflow-x-auto border-b border-[#1F1F1F]/8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
							{tabs.map(([key_1, label_1]) => <button key={key_1} type="button" role="tab" aria-selected={infoTab === key_1} data-testid={`product-tab-${key_1}`} onClick={() => setInfoTab(key_1)} className={cn("relative h-8 shrink-0 whitespace-nowrap pt-1.5 text-[16px] leading-[21px] outline-none transition-colors", infoTab === key_1 ? "font-semibold text-[#1F1F1F] after:absolute after:inset-x-0 after:bottom-0 after:h-px after:rounded-full after:bg-[#1F1F1F]" : "font-medium text-[#1F1F1F]/48 hover:text-[#1F1F1F]")}>
									{label_1}
								</button>)}
						</div>

						{infoTab === "specs" && <div className="flex flex-col gap-3">
								{(() => {
                    const colorLabel = t("product.color");
                    // Документ о соответствии (17.10.2026): «№ …, до …» со ссылкой на реестр, без файла.
                    // Ключ строки - вид документа (31.10.2026): покупатель видит, что это - сертификат, декларация или СГР.
                    const doc = product.complianceDocument;
                    const complianceLabel = doc ? t(`product.complianceKinds.${doc.kind}`, {
                      defaultValue: t("product.complianceLabel")
                    }) : t("product.complianceLabel");
                    const complianceValue = doc ? doc.validUntil ? t("product.complianceValue", {
                      number: doc.number,
                      date: doc.validUntil.split("-").reverse().join(".")
                    }) : t("product.complianceValueNoDate", {
                      number: doc.number
                    }) : "";
                    const filled = (list_1: [string, string | undefined][]) => list_1.filter((row_2): row_2 is [string, string] => Boolean(row_2[1]?.trim()));
                    const mainRows = filled([[t("product.productType", {
                      defaultValue: "Тип товара"
                    }), productType], ...(product.madeToOrderLeadTimeDays && (product.madeToOrder || customizable) ? [[t("product.productionTime"), t("product.productionDays", {
                      count: product.madeToOrderLeadTimeDays
                    })]] as [string, string][] : []), [t("product.category"), spec.category], [t("product.specType", {
                      defaultValue: "Тип"
                    }), spec.type], [colorLabel, selectedVariant?.color ?? product.color],
                    // Выбранный материал / свой параметр (fd-catalog-07).
                    ...axes.map((axis_0): [string, string | undefined] => [axis_0.label, axisPicks[axis_0.key]]), [attributeLabel("gender", i18n.language), genderValue], [complianceLabel, complianceValue]]);
                    const extraRows = filled([
                    // Свои характеристики продавца (fd-catalog-06, до 10 строк) - первыми
                    // под «Показать ещё», как остальные характеристики сверх макета.
                    ...(product.customAttributes ?? []).map((attr): [string, string] => [attr.label, attr.value]), ...attributes, ...(product.noWarranty ? [[t("product.warranty"), t("product.noWarranty")]] as [string, string][] : product.warrantyDays ? [[t("product.warranty"), t("product.warrantyDays", {
                      count: product.warrantyDays
                    })]] as [string, string][] : []), [t("product.article"), product.article]]);
                    const shown = showAllAttrs ? [...mainRows, ...extraRows] : mainRows;
                    return <>
											{/* Строки как в макете: ключ 133 w600 (длинный ключ — шире, без
               переноса), зазор 16, значение w500, оба #161616 14/18; шаг 30.
               «Показать ещё» — в колонке значений. */}
											<dl className="grid grid-cols-[minmax(0,45%)_minmax(0,1fr)] gap-x-4 sm:grid-cols-[minmax(133px,max-content)_minmax(0,1fr)] gap-y-3 text-[14px] leading-[18px] text-[#161616]">
												{shown.map(([key_2, value_1]) => <div key={key_2} className="contents">
														<dt className="font-semibold sm:whitespace-nowrap">{key_2}</dt>
														<dd className="flex min-w-0 items-center gap-1.5 font-medium">
															{key_2 === colorLabel && <ColorSwatches colors={[value_1]} />}
															{/* «Доступна кастомизация» открывает таблицу изменений снова — иначе
                   после «В корзину» (кнопка стала счётчиком) к ней не вернуться. */}
															{key_2 === complianceLabel && doc?.checkUrl ? <a href={doc.checkUrl} target="_blank" rel="noreferrer" title={t("product.complianceCheck")} data-testid="product-compliance" className="min-w-0 break-words underline decoration-[#161616]/30 underline-offset-4 outline-none transition-colors hover:text-osmix-tangelo focus-visible:text-osmix-tangelo">
																	{value_1}
																</a> : value_1 === productType && hasCustomization && cartLine ? <button type="button" onClick={openCustomization} data-testid="open-customization" className="min-w-0 break-words text-left underline decoration-[#161616]/30 underline-offset-4 outline-none transition-colors hover:text-osmix-tangelo focus-visible:text-osmix-tangelo">
																	{value_1}
																</button> : <span className="min-w-0 break-words">{value_1}</span>}
														</dd>
													</div>)}
											{extraRows.length > 0 && <button type="button" onClick={() => setShowAllAttrs(v_11 => !v_11)} className="col-start-2 flex w-fit items-center gap-2 text-[14px] font-semibold leading-[18px] text-[#1F1F1F] outline-none transition-colors hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">
													{showAllAttrs ? t("product.showLess") : t("product.showMore")}
													<ChevronDown16Icon className={cn("transition-transform", showAllAttrs && "rotate-180")} />
												</button>}
											</dl>
										</>;
                  })()}
							</div>}

						{infoTab === "description" && <div className="flex flex-col gap-3">
								{richBlocks.length > 0 ? <div className="flex flex-col gap-2">
										{richBlocks.map((block_0, index_0) => <RichDescriptionBlock key={`${block_0.type}-${index_0}`} block={block_0} />)}
									</div> : <p className="whitespace-pre-line type-body1 text-osmix-text-2">{product.description}</p>}
								{product.videoUrl && <a href={product.videoUrl} target="_blank" rel="noreferrer" className="w-fit type-body2 text-osmix-tangelo underline">
										{t("product.video")}
									</a>}
								{product.customization?.enabled && <p className="type-body2 text-osmix-text-2">{t("product.customization")}</p>}
								<p className="type-body2 text-osmix-text-2">
									{product.returnPolicy?.customOrderException ? t("product.returnsCustomException") : t("product.returnsStandard")}
								</p>
								{chips.length > 0 && <div className="flex flex-wrap gap-2" aria-label={t("product.tags")}>
										{chips.map(chip => <span key={chip} className="rounded-[4px] bg-osmix-gray-200 px-3 py-1 type-body2-bold text-osmix-text-1">
												{chip}
											</span>)}
									</div>}
							</div>}

						{/* Как этот продавец доставит товар в город покупателя (кадр 4052:41290). */}
						{infoTab === "delivery" && <ProductDelivery productId={product.id} variantId={selectedVariant?.id} />}

						{/* О магазине (кадр 4052:40245): фото и название — ссылки на магазин. */}
						{infoTab === "shop" && (seller ? <ProductShopTab shop={{
                  ...seller,
                  logoUrl: shop?.logoUrl
                }} category={product.categoryId ? {
                  id: product.categoryId,
                  name: product.category
                } : undefined} onMessage={openSellerChat} /> : <button type="button" onClick={openSellerChat} className="w-fit type-body2 text-osmix-tangelo transition-colors hover:underline">
									{t("product.messageSeller")}
								</button>)}
					</div>
				</div>
			</div>

			{hasCustomization && <CustomizationSheet open={customOpen} onOpenChange={setCustomOpen} options={customizationOptions} picked={customQty} onPick={(name, next_5) => setCustomQty(prev_0 => ({
            ...prev_0,
            [name]: next_5
          }))} unitPrice={displayPrice} qty={cartLine?.qty ?? 0} onQty={delta_0 => void changeQty(delta_0)} qtyBusy={qtyBusy || applying} canAddMore={Boolean(cartLine) && (cartLine?.qty ?? 0) < Math.max(cartLine?.stock ?? 0, 1)} applying={applying} onChat={() => {
            setCustomOpen(false);
            openSellerChat();
          }} />}
			{customizable && !hasCustomization && <CustomizeInChatDialog open={chatHintOpen} onOpenChange={setChatHintOpen} onChat={() => {
            setChatHintOpen(false);
            openSellerChat();
          }} />}

			{/* Купленный и не оценённый товар — полоса «Оцените товар» (кадр 3869:20098). */}
			<RatePurchasedBar productId={product.id} />

			<ProductReviewsPanel productId={product.id}
          // «бренд название», как в кадре («UNIQLET Куртка»); название, уже
          // начинающееся с бренда, не дублируем.
          title={product.name.toLowerCase().startsWith(product.brand.toLowerCase()) ? product.name : `${product.brand} ${product.name}`.trim()} colors={[...variants.map(v_12 => v_12.color ?? ""), product.color ?? ""].filter(Boolean)} open={reviewsOpen} onOpenChange={setReviewsOpen} tab={socialTab} onTabChange={setSocialTab} />

			{/* Ленты под карточкой (кадр 3561:144759): заголовок по центру 20/23 w600
       #161616 (A-20), до содержимого 20, между лентами 40. */}

			{/* «Собери образ» — complete-the-look из подборки, где встречается товар:
       карточки 173 через 20, ряд по центру. */}
			{outfitItems.length > 0 && <RailSection title={t("product.outfitTitle", {
            defaultValue: "Собери образ"
          })}>
					<div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-5">
						{outfitItems.map(p_3 => <div key={p_3.id} className="min-w-0 sm:w-[173px]">
								<OutfitCard product={p_3} />
							</div>)}
					</div>
				</RailSection>}

			{/* «Товары из этой коллекции» — остальной ассортимент магазина, лента 270. */}
			{collectionItems.length > 0 && <RailSection title={t("product.collectionTitle", {
            defaultValue: "Товары из этой коллекции"
          })}>
					<ProductRail products={collectionItems} />
				</RailSection>}

			{/* «Идеи с этим товаром» — подборки/образы, где участвует товар: плитки
       270×362, ряд по центру (A-19). */}
			{productIdeas.length > 0 &&
          // Все идеи скрыты покупателем — ленты нет совсем, а не пустой заголовок.
          <RailSection title={t("product.ideasTitle", {
            defaultValue: "Идеи с этим товаром"
          })} className="[&:not(:has(article))]:hidden">
					<div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-5">
						{productIdeas.map(idea => <UnlessHidden key={idea.id} idea={idea}>
								<div className="min-w-0 sm:w-[270px]">
									<ProductIdeaCard idea={idea} />
								</div>
							</UnlessHidden>)}
					</div>
				</RailSection>}

			{/* «Похожие товары» — семантически близкие из категории (не магазин). */}
			{similarItems.length > 0 && <RailSection title={t("product.similarTitle", {
            defaultValue: "Похожие товары"
          })}>
					<ProductRail products={similarItems} ads={recommendationAds.data ?? []} />
				</RailSection>}

			{/* «Похожие Идеи с этим товаром» — стена идей 270×362 (горизонтальная
       обложка — на две колонки), как в кадре (A-19). */}
			{similarIdeas.length > 0 && <RailSection title={t("product.similarIdeasTitle", {
            defaultValue: "Похожие Идеи с этим товаром"
          })} className="[&:not(:has(article))]:hidden">
					<IdeaWall>
						{similarIdeas.map(idea_0 => <UnlessHidden key={idea_0.id} idea={idea_0}>
								<ProductIdeaCard idea={idea_0} wall />
							</UnlessHidden>)}
					</IdeaWall>
				</RailSection>}

			{/* «Подборка от OSMIX» — свежая выдача каталога, сетка 4 × 3. */}
			{osmixItems.length > 0 && <RailSection title={t("product.osmixPickTitle", {
            defaultValue: "Подборка от OSMIX"
          })}>
					<ProductGrid products={osmixItems} />
				</RailSection>}
			</div>
		</div>
		</DiscoverySourceProvider>
  );
}

// Идея, которую покупатель скрыл (на шлюзе или в этой сессии), на карточке
// товара не рисуется.
function UnlessHidden(t0) {
  const {
    idea,
    children
  } = t0;
  return useIdeaHidden(idea) ? null : children;
}

// Лента под карточкой: заголовок по центру (20/23 w600 #161616, A-20), до
// содержимого 20px.
function RailSection(t0) {
  const $ = _c(8);
  const {
    title,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-w-0 flex-col gap-5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <h2 className="text-center text-[20px] font-semibold leading-[23px] tracking-[-0.31px] text-[#161616]">{title}</h2>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t2) {
    t3 = <section className={t1}>{t2}{children}</section>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

// Горизонтальная лента карточек (кадр: «idea feed» 270 через 20, уходит за правый
// край страницы и листается). На телефоне видно две карточки с краем третьей.
// С рекламой (fd-promo-26) рекламные карточки встают на места 1, 5, 9 … с
// меткой «Реклама», показы и клики уходят в статистику кампании — как в
// каталоге; рекламный товар из органики убирается, чтобы не повторяться.
function ProductRail(t0) {
  const $ = _c(9);
  const {
    products,
    ads: t1
  } = t0;
  let t2;
  let t3;
  let t4;
  if ($[0] !== products || $[1] !== t1) {
    const ads = t1 === undefined ? [] : t1;
    t2 = ads.length > 0 ? "product-rail-ads" : undefined;
    t3 = "-mx-4 flex snap-x gap-2 overflow-x-auto scroll-px-4 px-4 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-5 md:-mx-8 md:scroll-px-8 md:px-8 [&::-webkit-scrollbar]:hidden";
    t4 = mixAds(withoutAdProducts(products, ads), ads).map(_temp);
    $[0] = products;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <div data-testid={t2} className={t3}>{t4}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}

// Плитка идеи под карточкой товара (кадр 3561:144759, «idea card» 270×362):
// фото r4, сердечко 32 в углу, бейдж «сумка + число товаров» (белый 50 %, r4,
// 14/18) — без бейджа суммы, его в кадре нет. Данные — и участие товара в
// подборках, и общая лента идей: нужны только slug, обложка и число товаров.
// На стене (`wall`) горизонтальная обложка занимает две колонки, как в ленте.
function _temp(item) {
  return item.kind === "ad" ? <DiscoverySourceProvider key={`ad:${item.ad.campaignId}:${item.ad.productId}`} value="ads"><div className="w-[calc((100%-8px)/2.2)] shrink-0 snap-start sm:w-[270px]"><ProductCard product={item.ad.product} ad={{
        campaignId: item.ad.campaignId,
        placement: "recommendations"
      }} /></div></DiscoverySourceProvider> : <div key={item.item.id} className="w-[calc((100%-8px)/2.2)] shrink-0 snap-start sm:w-[270px]"><ProductCard product={item.item} /></div>;
}
function ProductIdeaCard(t0) {
  const $ = _c(23);
  const {
    idea: c,
    wall: t1
  } = t0;
  const wall = t1 === undefined ? false : t1;
  const [wide, setWide] = useState(false);
  const t2 = wall ? "size-full" : "aspect-[270/362]";
  const t3 = wall && wide && "col-span-2";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cn("relative min-w-0", t2, t3);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = `/feed/${c.slug}`;
  let t6;
  if ($[3] !== c.cover || $[4] !== c.title || $[5] !== wall) {
    t6 = c.cover && <img src={c.cover} alt={c.title} loading="lazy" onLoad={wall ? e => setWide(e.currentTarget.naturalWidth > e.currentTarget.naturalHeight) : undefined} className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />;
    $[3] = c.cover;
    $[4] = c.title;
    $[5] = wall;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <BagIcon size={20} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== c.productCount) {
    t8 = <span className="absolute bottom-2 left-2 flex h-6 items-center gap-[3px] rounded-[4px] bg-white/50 px-1 text-[14px] leading-[18px] text-[#1F1F1F]">{t7}{c.productCount}</span>;
    $[8] = c.productCount;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t5 || $[11] !== t6 || $[12] !== t8) {
    t9 = <Link to={t5} className="group isolate block size-full overflow-hidden rounded-[4px] bg-osmix-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t6}{t8}</Link>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== c) {
    t10 = ideaLikeId(c);
    $[14] = c;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== c.title || $[17] !== t10) {
    t11 = <SaveHeartButton id={t10} title={c.title} className="absolute right-2 top-2 size-8" />;
    $[16] = c.title;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t4 || $[21] !== t9) {
    t12 = <article className={t4}>{t9}{t11}</article>;
    $[19] = t11;
    $[20] = t4;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function RichDescriptionBlock(t0) {
  const $ = _c(6);
  const {
    block
  } = t0;
  if (block.type === "heading") {
    let t1;
    if ($[0] !== block.text) {
      t1 = <h2 className="type-body1-bold text-osmix-text-1">{block.text}</h2>;
      $[0] = block.text;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (block.type === "bullet") {
    let t1;
    if ($[2] !== block.text) {
      t1 = <p className="type-body1 text-osmix-text-2">• {block.text}</p>;
      $[2] = block.text;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== block.text) {
    t1 = <p className="whitespace-pre-line type-body1 text-osmix-text-2">{block.text}</p>;
    $[4] = block.text;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
type ProductMediaItem = {
  type: "photo";
  src: string;
} | {
  type: "video";
  src: string;
};
function videoEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${parsed.pathname.slice(1)}`;
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = parsed.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (parsed.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${parsed.pathname.split("/")[2]}`;
      if (parsed.pathname.startsWith("/embed/")) return url;
    }
  } catch {
    return null;
  }
  return null;
}
const isDirectVideo = (url: string): boolean => /\.(mp4|webm|ogg)(\?|#|$)/i.test(url);
function VideoFrame(t0) {
  const $ = _c(13);
  const {
    src,
    title
  } = t0;
  let t1;
  if ($[0] !== src) {
    t1 = videoEmbedUrl(src);
    $[0] = src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const embed = t1;
  if (isDirectVideo(src)) {
    let t2;
    if ($[2] !== src) {
      t2 = <video src={src} controls={true} className="size-full bg-black object-contain" />;
      $[2] = src;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (embed) {
    let t2;
    if ($[4] !== embed || $[5] !== title) {
      t2 = <iframe src={embed} title={title} className="size-full border-0 bg-black" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen={true} />;
      $[4] = embed;
      $[5] = title;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Play className="size-10" fill="currentColor" />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== title) {
    t3 = <span className="type-body2 underline">{title}</span>;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== src || $[11] !== t3) {
    t4 = <a href={src} target="_blank" rel="noreferrer" className="flex size-full flex-col items-center justify-center gap-3 text-osmix-tangelo">{t2}{t3}</a>;
    $[10] = src;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function ProductSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="aspect-[560/728] w-full rounded-[4px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="grid gap-8 lg:grid-cols-2">{t0}<div className="flex flex-col gap-4"><Skeleton className="h-4 w-24" /><Skeleton className="h-9 w-3/4" /><Skeleton className="h-8 w-32" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}